import {Modal} from '@/components/atoms';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import {useMemo, useState, useRef, useEffect, useCallback} from 'react';
import {useRewards} from '@/hooks/useRewards';
import PropTypes from 'prop-types';
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper';
import {cellDataMapperObj} from '@/containers/SalesTarget/SalesTargetList';
import {useNavigate} from 'react-router-dom';
import {SegmentsColConfig} from '@/services/MockData/SegmentsList/SegmentsColConfig';
import CustomDataTable from '@/components/organisms/CustomDataTable/CustomDataTable';

/**
 * SegmentsModal
 * - Displays a modal with searchable, sortable, paginated segments table.
 * - Supports persistent multi-page selection.
 */

const SegmentsModal = ({
  show,
  onClose,
  onApply,
  title = '',
  selectedSegments = [],
  initialSelectedIds = [],
  singleSelectMode = false
}) => {
  const navigate = useNavigate();

  // Local state
  const [rowSelected, setRowSelected] = useState([]);
  const [tableKey, setTableKey] = useState(0);
  const [isCancel, setIsCancel] = useState(false);
  const selectedMapRef = useRef(new Map());
  const isMounted = useRef(false);
  const prevShowRef = useRef(false);
  const initializedRef = useRef(false);

  // Table state (via hook)
  const {
    segmentsApiData,
    segmentsTotal,
    segmentsApiLoading,
    pageIndex,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    handleResetAll
  } = useRewards();

  const columns = useMemo(
    () => getCellDataMappedCols(cellDataMapperObj(navigate), SegmentsColConfig),
    [navigate]
  );

  /** ---------- Data Transformation ---------- */
  // Transform segments data to ensure consistent id field
  const transformedSegmentsData = useMemo(
    () =>
      segmentsApiData.map((segment, index) => ({
        ...segment,
        id:
          segment.segmentName ||
          segment.id ||
          segment.segmentId ||
          `segment-${index}`
      })),
    [segmentsApiData]
  );

  /** ---------- Helper Functions ---------- */
  const initializeSelections = useCallback(() => {
    if (!prevShowRef.current || !initializedRef.current) {
      const list = Array.isArray(selectedSegments) ? selectedSegments : [];

      const normalized = list.map((seg) => {
        if (typeof seg === 'string') {
          return {segmentName: seg, id: seg};
        }

        return {
          segmentName: seg.segmentName ?? seg.id ?? seg.value ?? '',
          id: seg.segmentName ?? seg.id ?? seg.value ?? '',
          ...seg
        };
      });

      // Build selection map keyed by segmentName (using as id)
      selectedMapRef.current = new Map(
        normalized.filter((seg) => Boolean(seg.id)).map((seg) => [seg.id, seg])
      );

      // Initialize selectedMapRef with initialSelectedIds
      if (Array.isArray(initialSelectedIds) && initialSelectedIds.length > 0) {
        initialSelectedIds.forEach((id) => {
          if (!selectedMapRef.current.has(id)) {
            selectedMapRef.current.set(id, {
              id,
              segmentName: id,
              isPlaceholder: true
            });
          }
        });
      }

      initializedRef.current = true;
    }
  }, [selectedSegments, initialSelectedIds]);

  const updatePlaceholdersWithFullData = useCallback(() => {
    if (transformedSegmentsData.length > 0) {
      let hasUpdates = false;

      // Replace placeholder objects with full data when items appear on current page
      transformedSegmentsData.forEach((segment) => {
        if (selectedMapRef.current.has(segment.id)) {
          const existing = selectedMapRef.current.get(segment.id);
          if (existing?.isPlaceholder) {
            selectedMapRef.current.set(segment.id, segment);
            hasUpdates = true;
          }
        }
      });

      // Update rowSelected only when there are actual updates or when initializing
      if (
        hasUpdates ||
        (!prevShowRef.current && selectedMapRef.current.size > 0)
      ) {
        const allSelected = Array.from(selectedMapRef.current.values());
        setRowSelected(allSelected);
      }
    }
  }, [transformedSegmentsData]);

  /** ---------- Optimized useEffect ---------- */
  useEffect(() => {
    // Handle modal opening/closing and initialization
    if (show) {
      // Reset table key when modal opens (forces re-init)
      if (!prevShowRef.current) {
        setTableKey((prev) => prev + 1);
        isMounted.current = true;
        initializedRef.current = false;
      }

      initializeSelections();
      updatePlaceholdersWithFullData();

      // Initialize rowSelected when modal first opens and we have selections
      if (!prevShowRef.current && selectedMapRef.current.size > 0) {
        const allSelected = Array.from(selectedMapRef.current.values());
        setRowSelected(allSelected);
      }
    } else {
      // Modal is closing
      isMounted.current = false;
      initializedRef.current = false;
    }

    // Update prevShowRef for next render
    prevShowRef.current = show;
  }, [show, initializeSelections, updatePlaceholdersWithFullData]);

  /** ---------- Handlers ---------- */
  // Handle row selection with persistent cross-page selection
  const handleRowSelect = (selectedRowsData) => {
    const newSelected = selectedRowsData || [];

    const newIds = new Set(newSelected.map((r) => r.id).filter(Boolean));
    const currentPageIds = new Set(
      transformedSegmentsData.map((r) => r.id).filter(Boolean)
    );

    // Remove unselected IDs from current page
    currentPageIds.forEach((id) => {
      if (!newIds.has(id)) {
        selectedMapRef.current.delete(id);
      }
    });

    // Add new selected IDs
    newIds.forEach((id) => {
      const record = transformedSegmentsData.find((s) => s.id === id);
      if (record) selectedMapRef.current.set(id, record);
    });

    // Refresh visible selection for current page
    const pageSelected = transformedSegmentsData.filter((s) => {
      const id = s.id;
      return id && selectedMapRef.current.has(id);
    });
    setRowSelected(pageSelected);
  };

  const handleSelectSegments = () => {
    const selectedRecords = Array.from(selectedMapRef.current.values());

    // Format data to match previous handleApply structure
    const ids = new Set(selectedRecords.map((seg) => seg.id).filter(Boolean));
    const items = selectedRecords.map((seg) => ({
      id: seg.id,
      label: seg.segmentName || seg.label || seg.name || ''
    }));
    onApply({ids, items});
    handleCloseModal();
  };

  const handleCloseModal = () => {
    selectedMapRef.current = new Map();
    setRowSelected([]);
    setTableKey(0);
    onClose();
  };

  const handleCancelClick = () => {
    setIsCancel(true);
  };

  const handleCancelConfirm = () => {
    setIsCancel(false);
    handleCloseModal();
  };

  const handleCancelAbort = () => {
    setIsCancel(false);
    // Restore rowSelected from selectedMapRef to show all selections across pages
    const allSelected = Array.from(selectedMapRef.current.values());
    setRowSelected(allSelected);
  };

  // Check if multiple segments are selected in single select mode
  const hasMultipleSelections =
    singleSelectMode &&
    selectedMapRef.current &&
    selectedMapRef.current.size > 1;
  const isSelectDisabled =
    !(selectedMapRef.current && selectedMapRef.current.size > 0) ||
    hasMultipleSelections;

  // Dynamic modal configuration based on state
  const getModalConfig = () => {
    if (isCancel) {
      // Cancel confirmation modal configuration
      return {
        showModal: isCancel,
        type: MODAL_TYPE.SMALL,
        className: 'segments-modal',
        title: 'Are you sure you want to cancel?',
        text: 'The selection will not be saved.',
        isDark: true,
        showPrimaryButton: true,
        primaryButtonText: 'Yes',
        onClickPrimaryButton: handleCancelConfirm,
        showSecondaryButton: true,
        secondaryButtonText: 'No',
        onClickSecondaryButton: handleCancelAbort,
        onCloseModal: handleCancelAbort,
        footerProps: {
          size: 'medium',
          type: 'primary'
        }
      };
    }

    // Main segments modal configuration
    return {
      type: MODAL_TYPE.SMALL,
      className: 'segments-modal',
      hideCloseButton: true,
      showModal: show,
      title: title,
      isDark: true,
      showSecondaryButton: true,
      secondaryButtonText: 'Cancel',
      onClickSecondaryButton: handleCancelClick,
      showPrimaryButton: true,
      primaryButtonText: 'Apply',
      onClickPrimaryButton: handleSelectSegments,
      primaryButtonDisabled: isSelectDisabled,
      content: (
        <div className='flex max-h-[70vh] flex-col overflow-y-auto'>
          {/* Warning message when multiple segments are selected in single select mode */}
          {singleSelectMode && hasMultipleSelections && (
            <div className='mb-4 flex items-center rounded bg-orange-600 p-3 text-white'>
              <span className='material-icons mr-2'>warning</span>
              <span>Please select only one segment to continue.</span>
            </div>
          )}
          <CustomDataTable
            key={tableKey}
            columns={columns}
            records={transformedSegmentsData}
            isLoading={segmentsApiLoading}
            pageSize={10}
            totalRecords={segmentsTotal}
            paginationConfig={{
              pageIndex,
              setPageIndex
            }}
            sortingConfig={{
              handleSorting: setSortingObj
            }}
            filterConfig={{
              setSearchArry,
              onResetAll: handleResetAll
            }}
            selectionConfig={{
              onRowSelect: handleRowSelect,
              initialRowSelection: rowSelected,
              hideSelectAll: true,
              globalSelectionEnable: true,
              resetSelectionOnFilter: false
            }}
            toolbarConfig={{
              hideToolbar: true,
              showActionMenu: false
            }}
            displayConfig={{
              tableMinWidth: 400
            }}
          />
        </div>
      )
    };
  };

  const modalConfig = getModalConfig();

  return <Modal {...modalConfig} />;
};

SegmentsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  title: PropTypes.string,
  selectedSegments: PropTypes.array,
  initialSelectedIds: PropTypes.array,
  singleSelectMode: PropTypes.bool
};

export default SegmentsModal;
